import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { useProviderInfo } from '../hooks/useProviderInfo';
import { ProviderEventRow } from '../types/foundObject';

const emptyForm = {
  name: '',
  year: new Date().getFullYear(),
  starts_at: '',
  ends_at: '',
  is_active: true,
};

const ProviderEventsPage: React.FC = () => {
  const theme = useTheme();
  const dialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderEventRow | null>(null);
  const [form, setForm] = useState(emptyForm);

  const { data: providerInfo } = useProviderInfo();

  const annual = Boolean(providerInfo?.provider_type?.annual_event);

  const { data: eventsRes, isLoading } = useQuery({
    queryKey: ['provider-events'],
    queryFn: () => apiService.getProviderEvents(),
    enabled: annual,
  });

  const events = eventsRes?.data?.provider_events ?? [];

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing) {
        return apiService.updateProviderEvent(editing.id, {
          name: form.name,
          year: form.year,
          starts_at: form.starts_at,
          ends_at: form.ends_at,
          is_active: form.is_active,
        });
      }
      return apiService.createProviderEvent({
        name: form.name,
        year: form.year,
        starts_at: form.starts_at,
        ends_at: form.ends_at,
        is_active: form.is_active,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-events'] });
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteProviderEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-events'] });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      year: new Date().getFullYear(),
    });
    setDialogOpen(true);
  };

  const openEdit = (ev: ProviderEventRow) => {
    setEditing(ev);
    setForm({
      name: ev.name,
      year: ev.year,
      starts_at: ev.starts_at,
      ends_at: ev.ends_at,
      is_active: ev.is_active,
    });
    setDialogOpen(true);
  };

  if (!annual) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
        <Alert severity="info">
          Votre type de partenaire n&apos;est pas configuré comme « événement annuel ». Contactez
          l&apos;administrateur Sherlook pour activer cette option sur votre type (ex. festival,
          manifestation sportive).
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
        mb={3}
      >
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.2rem', sm: '2rem' } }}>
          Événements (éditions annuelles)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
        >
          Nouvelle édition
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Créez une ligne par édition (ex. « Festival XYZ 2026 ») avec les dates réelles. Les objets
        trouvés pourront être rattachés à cette édition.
      </Typography>

      {isLoading ? (
        <Typography>Chargement…</Typography>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}
        >
          <Table size="small" sx={{ minWidth: 520 }}>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Année</TableCell>
                <TableCell>Début</TableCell>
                <TableCell>Fin</TableCell>
                <TableCell>Actif</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((ev) => (
                <TableRow key={ev.id}>
                  <TableCell>{ev.name}</TableCell>
                  <TableCell>{ev.year}</TableCell>
                  <TableCell>{ev.starts_at}</TableCell>
                  <TableCell>{ev.ends_at}</TableCell>
                  <TableCell>
                    {ev.is_active ? (
                      <Chip size="small" label="Oui" color="success" />
                    ) : (
                      <Chip size="small" label="Non" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(ev)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        if (window.confirm('Supprimer cet événement ?')) {
                          deleteMutation.mutate(ev.id);
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={dialogFullScreen}
      >
        <DialogTitle>{editing ? 'Modifier l’édition' : 'Nouvelle édition'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nom (ex. Festival de musique 2026)"
            fullWidth
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <TextField
            label="Année"
            type="number"
            fullWidth
            value={form.year}
            onChange={(e) => setForm((f) => ({ ...f, year: parseInt(e.target.value, 10) || f.year }))}
          />
          <TextField
            label="Début"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.starts_at}
            onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
          />
          <TextField
            label="Fin"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.ends_at}
            onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              />
            }
            label="Édition visible (liste publique)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={!form.name || !form.starts_at || !form.ends_at || saveMutation.isPending}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProviderEventsPage;
